import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, X, Trash2, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";
import AddLinkDialog from "@/components/AddLinkDialog";

export default function LinksPage() {
  const [links, setLinks] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("suggested"); // suggested | public | all
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    const [l, c] = await Promise.all([api.get("/links"), api.get("/categories")]);
    setLinks(l.data);
    setCats(c.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await api.post(`/links/${id}/approve`);
    toast.success("Published to public page");
    load();
  };
  const approveAllAi = async () => {
    const r = await api.post("/links/bulk-approve", { all_ai: true });
    toast.success(`Published ${r.data.approved} link${r.data.approved === 1 ? "" : "s"}`);
    load();
  };
  const del = async (id) => {
    await api.delete(`/links/${id}`);
    toast.success("Deleted");
    load();
  };
  const update = async (id, patch) => {
    await api.put(`/links/${id}`, patch);
    load();
  };
  const togglePublic = async (id, current) => {
    await api.put(`/links/${id}`, { is_public: !current });
    load();
  };

  const filtered = links.filter((l) => {
    if (filter === "suggested") return l.is_ai_suggested && !l.is_public;
    if (filter === "public") return l.is_public;
    return true;
  });

  return (
    <div className="p-10 max-w-6xl">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Manage</div>
          <h1 className="font-heading font-light text-4xl tracking-tighter mt-2">Links</h1>
        </div>
        <div className="flex items-center gap-2">
          {filter === "suggested" && links.filter(l => l.is_ai_suggested && !l.is_public).length > 0 && (
            <button
              data-testid="approve-all-ai-btn"
              onClick={approveAllAi}
              className="inline-flex items-center gap-2 border border-zinc-200 text-zinc-950 text-sm px-4 py-2.5 hover:bg-zinc-50 rounded-sm"
            >
              <Check className="w-3.5 h-3.5" /> Approve all AI suggestions
            </button>
          )}
          <button
            data-testid="add-link-open-btn"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm px-4 py-2.5 hover:bg-zinc-800 rounded-sm"
          >
            <Plus className="w-3.5 h-3.5" /> Add link
          </button>
        </div>
      </div>

      <AddLinkDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={load}
        categories={cats}
      />

      <div className="flex gap-1 mb-6 border-b border-zinc-200">
        {[
          { k: "suggested", label: "AI Suggested" },
          { k: "public", label: "Live" },
          { k: "all", label: "All" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`links-tab-${t.k}`}
            onClick={() => setFilter(t.k)}
            className={`px-4 py-2.5 text-sm border-b-2 transition-colors -mb-px ${
              filter === t.k ? "border-zinc-950 text-zinc-950 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-950"
            }`}
          >
            {t.label} <span className="text-xs text-zinc-400 ml-1">
              {links.filter(l => t.k === "suggested" ? (l.is_ai_suggested && !l.is_public) : t.k === "public" ? l.is_public : true).length}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-zinc-200 p-12 text-center text-sm text-zinc-500">
          Nothing here yet.
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-zinc-200 text-xs tracking-[0.15em] uppercase text-zinc-500">
            <div className="col-span-5">Link</div>
            <div className="col-span-3">Category</div>
            <div className="col-span-1">Clicks</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          <div className="divide-y divide-zinc-100">
            {filtered.map((l) => (
              <div key={l.id} data-testid={`link-row-${l.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-zinc-50 transition-colors">
                <div className="col-span-5 min-w-0">
                  <input
                    data-testid={`link-title-${l.id}`}
                    defaultValue={l.title}
                    onBlur={(e) => e.target.value !== l.title && update(l.id, { title: e.target.value })}
                    className="w-full font-medium bg-transparent border-0 outline-none focus:bg-white focus:ring-1 focus:ring-zinc-300 px-2 py-1 -ml-2 rounded-sm"
                  />
                  <a href={l.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-zinc-500 hover:text-zinc-950 truncate flex items-center gap-1 mt-0.5 ml-0">
                    <span className="truncate">{l.url}</span>
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                </div>
                <div className="col-span-3">
                  <select
                    data-testid={`link-cat-${l.id}`}
                    value={l.category_id || ""}
                    onChange={(e) => update(l.id, { category_id: e.target.value || null })}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-sm px-2 py-1.5 focus:border-zinc-950 outline-none"
                  >
                    <option value="">Uncategorized</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="col-span-1 text-sm text-zinc-500 font-mono">{l.clicks || 0}</div>
                <div className="col-span-3 flex items-center gap-2 justify-end">
                  {l.is_public ? (
                    <button
                      data-testid={`link-unpublish-${l.id}`}
                      onClick={() => togglePublic(l.id, true)}
                      className="text-xs px-3 py-1.5 border border-zinc-200 hover:bg-zinc-100 rounded-sm"
                    >
                      Unpublish
                    </button>
                  ) : (
                    <button
                      data-testid={`link-approve-${l.id}`}
                      onClick={() => approve(l.id)}
                      className="text-xs px-3 py-1.5 bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm inline-flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Publish
                    </button>
                  )}
                  <button
                    data-testid={`link-delete-${l.id}`}
                    onClick={() => del(l.id)}
                    className="text-xs p-1.5 text-zinc-400 hover:text-red-500 rounded-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
